import React, { useRef, useState } from 'react';
import styled from 'styled-components';
import { Upload, Loader, X } from 'lucide-react';
import { fetchWithAuth } from '../../helpers';

interface ImageUploadProps {
  value: string;
  onChange: (imageUrl: string) => void;
}

export const ImageUpload: React.FC<ImageUploadProps> = ({ value, onChange }) => {
  const [isDragging, setIsDragging] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [imageError, setImageError] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Build full image URL
  const getFullImageUrl = (url: string) => {
    if (!url) return '';
    if (url.startsWith('http://') || url.startsWith('https://')) return url;
    return `${process.env.REACT_APP_API_URL}${url}`;
  };

  const handleFileSelect = async (file: File) => {
    if (!file.type.startsWith('image/')) {
      alert('Vui lòng chọn một tệp hình ảnh!');
      return;
    }

    // Kiểm tra kích thước (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      alert('Kích thước ảnh không được vượt quá 10MB!');
      return;
    }

    setIsUploading(true);
    setImageError(false);

    try {
      // Upload file lên server
      const formData = new FormData();
      formData.append('image', file);

      const response = await fetchWithAuth(
        `${process.env.REACT_APP_API_URL}/api/flashcards/cards/upload-image`,
        {
          method: 'POST',
          body: formData,
          // Không set Content-Type header, browser sẽ tự set với boundary
        }
      );

      const result = await response.json();

      if (result.success && result.data?.url) {
        // Trả về đường dẫn tương đối để lưu vào DB
        onChange(result.data.url);
      } else {
        throw new Error(result.message || 'Upload failed');
      }
    } catch (error: any) {
      console.error('Upload image error:', error);
      alert('Không thể upload ảnh. Vui lòng thử lại!');
    } finally {
      setIsUploading(false);
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const files = e.dataTransfer.files;
    if (files.length > 0) {
      handleFileSelect(files[0]);
    }
  };

  const handleClick = () => {
    if (!isUploading) {
      fileInputRef.current?.click();
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.currentTarget.files;
    if (files && files.length > 0) {
      handleFileSelect(files[0]);
    }
  };

  const handleRemoveImage = (e: React.MouseEvent) => {
    e.stopPropagation();
    onChange('');
    setImageError(false);
  };

  const handleImageError = () => {
    setImageError(true);
  };

  // Nếu có ảnh và không lỗi, hiển thị preview với nút xóa
  if (value && !imageError) {
    return (
      <ImagePreviewContainer>
        <ImagePreview 
          src={getFullImageUrl(value)} 
          alt="Preview" 
          onError={handleImageError}
        />
        <RemoveButton onClick={handleRemoveImage} title="Xóa ảnh">
          <X size={14} />
        </RemoveButton>
        <HiddenInput
          ref={fileInputRef}
          type="file"
          accept="image/*"
          onChange={handleInputChange}
          disabled={isUploading}
        />
      </ImagePreviewContainer>
    );
  }

  // Nếu không có ảnh hoặc ảnh lỗi, hiển thị dropzone
  return (
    <Container
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
      onClick={handleClick}
      $isDragging={isDragging}
      $isUploading={isUploading}
    >
      <IconWrapper>
        {isUploading ? <Loader size={16} className="spinning" /> : <Upload size={16} />}
      </IconWrapper>
      <UploadText>
        {isUploading ? "Đang upload..." : "Kéo thả hoặc click để chọn ảnh..."}
      </UploadText>
      <HiddenInput
        ref={fileInputRef}
        type="file"
        accept="image/*"
        onChange={handleInputChange}
        disabled={isUploading}
      />
    </Container>
  );
};

// Styled Components
const Container = styled.div<{ $isDragging: boolean; $isUploading: boolean }>`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  border: 1px dashed ${props => props.$isDragging ? '#3b82f6' : '#d1d5db'};
  border-radius: 6px;
  background: ${props => props.$isDragging ? '#eff6ff' : '#f9fafb'};
  cursor: ${props => props.$isUploading ? 'wait' : 'pointer'};
  transition: all 0.2s;
  opacity: ${props => props.$isUploading ? 0.7 : 1};

  &:hover {
    border-color: ${props => props.$isUploading ? '#d1d5db' : '#3b82f6'};
    background: ${props => props.$isUploading ? '#f9fafb' : '#eff6ff'};
  }

  .spinning {
    animation: spin 1s linear infinite;
  }

  @keyframes spin {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }
`;

const IconWrapper = styled.div`
  color: #6b7280;
  display: flex;
  align-items: center;
  flex-shrink: 0;
`;

const UploadText = styled.span`
  font-size: 12px;
  color: #6b7280;
`;

const HiddenInput = styled.input`
  display: none;
`;

const ImagePreviewContainer = styled.div`
  position: relative;
  display: inline-block;
  width: 100%;
  max-width: 150px;
  border-radius: 8px;
  overflow: hidden;
  border: 2px solid #e5e7eb;
  background: #f9fafb;
  
  &:hover {
    border-color: #3b82f6;
  }
`;

const ImagePreview = styled.img`
  width: 100%;
  height: 80px;
  object-fit: cover;
  display: block;
`;

const RemoveButton = styled.button`
  position: absolute;
  top: 4px;
  right: 4px;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: rgba(239, 68, 68, 0.9);
  border: none;
  color: white;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;
  box-shadow: 0 2px 4px rgba(0,0,0,0.2);

  &:hover {
    background: #dc2626;
    transform: scale(1.1);
  }
`;
